import { AgentMessagingService } from "./AgentMessagingService";
import { apiCall } from "./firebase";

export interface CoachState {
  agentId: string;
  coachType: string;
  exists?: boolean;
  reviewSessionActive: boolean;
  currentPlan: any;
  createdAt: Date | null;
  updatedAt: Date | null;
}

export interface CoachPlan {
  id: string;
  coachId: string;
  planType: "initial" | "weekly" | "monthly";
  goals: any;
  actionItems: any[];
  metrics: any;
  status: "draft" | "approved" | "in_progress" | "completed";
  createdAt: Date;
  updatedAt: Date;
}

export class CoachService {
  async getCoach(coachType: string): Promise<any> {
    const response = await apiCall("/getCoach", {
      method: "POST",
      body: JSON.stringify({ coachType }),
    });
    return response.coach;
  }

  async createCoach(coachType: string, name: string): Promise<any> {
    const response = await apiCall("/createCoach", {
      method: "POST",
      body: JSON.stringify({ coachType, name }),
    });

    // Invalidate agent cache since a new coach/agent was created
    AgentMessagingService.invalidateAgentCache();

    return response.coach;
  }

  async startReviewSession(coachType: string): Promise<any> {
    const response = await apiCall("/startReviewSession", {
      method: "POST",
      body: JSON.stringify({ coachType }),
    });

    // Invalidate agent cache since review session might create new agents
    AgentMessagingService.invalidateAgentCache();

    return response;
  }

  async getCoachState(agentId: string): Promise<CoachState> {
    const response = await apiCall(`/getCoachState?agentId=${agentId}`);
    return response.state;
  }

  async updateCoachPlan(agentId: string, plan: any): Promise<CoachPlan> {
    const response = await apiCall("/updateCoachPlan", {
      method: "POST",
      body: JSON.stringify({ agentId, plan }),
    });
    return response.plan;
  }

  async getUserCoaches(): Promise<{
    accessibleCoachTypes: string[];
    coaches: Array<{ coachType: string; hasActiveChat: boolean; agentId?: string; currentPlan?: any }>;
  }> {
    const response = await apiCall("/getUserCoaches");
    return {
      accessibleCoachTypes: response.accessibleCoachTypes || [],
      coaches: response.coaches || [],
    };
  }

  async getCoachPlans(agentId: string): Promise<CoachPlan[]> {
    const response = await apiCall(`/getCoachPlans?agentId=${agentId}`);
    return response.plans;
  }

  async saveCoachPlan(planData: {
    coachId: string;
    planType: "initial" | "weekly" | "monthly";
    goals?: any;
    actionItems?: any[];
    metrics?: any;
    status?: "draft" | "approved" | "in_progress" | "completed";
  }): Promise<CoachPlan> {
    const response = await apiCall("/saveCoachPlan", {
      method: "POST",
      body: JSON.stringify(planData),
    });
    return response.plan;
  }

  async getCurrentPlan(agentId: string): Promise<CoachPlan | null> {
    try {
      const coachState = await this.getCoachState(agentId);
      return coachState.currentPlan || null;
    } catch (error) {
      console.error("Error getting current plan:", error);
      return null;
    }
  }
}

export const coachService = new CoachService();
